/**
 * kiaanApi — typed client for `/api/kiaan/*` (plan 2026-10-16-kiaan-in-aura, Tasks 3/4/8/9).
 *
 * The backend is built in a parallel lane. Every call here can fail; callers
 * show `KIAAN_UNREACHABLE` and keep the AURA page working. Short timeout so a
 * slow Kiaan Work never holds the widget.
 *
 * The main `api` client has baseURL `/api/v1`; Kiaan routes live at `/api/kiaan`,
 * so this file has its own axios instance (same cookie session).
 */
import axios from 'axios';

export const KIAAN_UNREACHABLE = 'Kiaan Work is not reachable. AURA works as normal.';
export const KIAAN_NO_ACCESS = 'This login cannot open this part of Kiaan. Ask your admin.';

/** 403 = this login is not allowed (not an outage). Anything else = unreachable. */
export function kiaanErrorText(err: unknown): string {
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  return status === 403 ? KIAAN_NO_ACCESS : KIAAN_UNREACHABLE;
}

export const kiaanHttp = axios.create({
  baseURL: '/api/v1/kiaan',
  withCredentials: true,
  timeout: 4000,
  headers: { 'Content-Type': 'application/json' },
});

function unwrap<T>(body: unknown): T {
  const b = body as { data?: unknown } | undefined;
  return (b && typeof b === 'object' && 'data' in b ? (b as { data: T }).data : (body as T));
}

// ── Agents (Kiaan tab) ──────────────────────────────────────────────────────

export type AgentStatus = 'working' | 'ready' | 'off';

export interface KiaanAgent {
  id: string;
  name: string;
  teamName: string;
  role: string; // 'Developer' | 'Lead' | 'Reviewer' | ...
  status?: AgentStatus;
  main?: boolean;
  /** SUN-169: stable role key from the agent link (manager, lead, dev, reviewer, browser); null = none. */
  agentRole?: string | null;
  /** SUN-169: what runs the agent. Labels only. */
  engine?: { kind: 'Claude' | 'Hermes'; model: string } | null;
  codeTree?: string[];
  knowledgePacks?: string[];
  /** The Agent computer (screen) is on for this agent. */
  computer?: boolean;
}

export interface KiaanFolder {
  name: string;
  access: 'read' | 'read_write' | 'none';
}

export interface KiaanStep {
  at: string; // "10:02"
  text: string;
  highlight?: boolean;
}

export interface KiaanAgentActivity {
  folders: KiaanFolder[];
  steps: KiaanStep[];
}

export interface KiaanAgentInput {
  name: string;
  job: string;
  teamName: string;
  bossAgentId?: string | null;
  brain: 'claude' | 'openrouter' | 'auto';
  monthlyLimit?: number | null;
  knowledgePacks: string[];
  codeTree?: string[];
  tools: { name: string; access: string }[];
  rules: { schedule: string; sendsMessage: string; changesMoney: string };
}

export async function listAgents(): Promise<KiaanAgent[]> {
  const res = await kiaanHttp.get('/agents');
  const list = unwrap<KiaanAgent[]>(res.data);
  return Array.isArray(list) ? list : [];
}

export async function getAgentActivity(agentId: string): Promise<KiaanAgentActivity> {
  const res = await kiaanHttp.get(`/agents/${encodeURIComponent(agentId)}/activity`);
  const a = unwrap<Partial<KiaanAgentActivity>>(res.data) ?? {};
  return { folders: a.folders ?? [], steps: a.steps ?? [] };
}

export async function createAgent(input: KiaanAgentInput): Promise<KiaanAgent> {
  const res = await kiaanHttp.post('/agents', input);
  return unwrap<KiaanAgent>(res.data);
}

export type AgentKindName = 'Assistant' | 'Developer';

/** The card Kiaan fills from one line (backend agent-draft.ts). */
export interface KiaanAgentDraft {
  kind: AgentKindName;
  icon: string;
  name: string;
  job: string;
  teamName: string;
  brain: KiaanAgentInput['brain'];
  monthlyLimit: number;
  knowledgePacks: string[];
  codeTree: string[];
  tools: { name: string; access: string }[];
  rules: { schedule: string; sendsMessage: string; changesMoney: string };
}

export interface KiaanDraftResult {
  draft: KiaanAgentDraft;
  /** Card lines that changed: top · team · reads · does · never · runs · brain · code */
  changed: string[];
  source: 'kiaan' | 'template';
  message: string;
}

export async function draftAgent(body: { line?: string; kind?: AgentKindName; change?: string; current?: KiaanAgentDraft; teams?: string[] }): Promise<KiaanDraftResult> {
  // Kiaan may ask the AI (server gives it 12 s), so this call waits longer than the usual 4 s.
  const res = await kiaanHttp.post('/agents/draft', { line: '', ...body }, { timeout: 20000 });
  return unwrap<KiaanDraftResult>(res.data);
}

export async function testAgent(input: KiaanAgentInput): Promise<{ ok: boolean; message: string }> {
  const res = await kiaanHttp.post('/agents/test', input);
  return unwrap<{ ok: boolean; message: string }>(res.data);
}

// ── Team (channels + messages) ──────────────────────────────────────────────

export interface KiaanMember {
  memberType: 'user' | 'agent';
  memberId: string;
  name: string;
}

export interface KiaanChannel {
  id: string;
  name: string;
  kind: 'channel' | 'direct';
  unread?: number;
  members?: KiaanMember[];
}

/** A file or picture sent in a chat (SUN-82). `url` is a short-lived signed link. */
export interface ChatAttachment {
  id: string;
  name: string;
  type: string;
  size: number;
  kind: 'image' | 'file';
  url?: string;
}

export interface KiaanMessage {
  id: string;
  channelId: string;
  /** `system` = a line Kiaan writes (chain limit, "Stop agents here"). */
  authorType: 'user' | 'agent' | 'system';
  authorId: string;
  authorName?: string;
  body: string;
  attachments?: ChatAttachment[];
  taskIssueId?: string | null;
  /** Agent replies that name a Kiaan Work issue (SUN-71), e.g. "SUN-72". */
  taskIdentifier?: string | null;
  taskTitle?: string | null;
  taskTeam?: string | null;
  createdAt: string;
}

/** POST answer: who was woken, and a small line for the sender (e.g. only admins can call agents). */
export interface KiaanPosted extends KiaanMessage {
  woken?: string[];
  notice?: string;
}

/** GET /channels/:id/agents (SUN-71). */
export interface ChannelAgentState {
  limit: number;
  chain: number;
  writing: string[];
  agents: Array<{ id: string; status: string | null; role: string }>;
}

export interface KiaanPerson {
  id: string;
  name: string;
}

export async function listChannels(): Promise<KiaanChannel[]> {
  const res = await kiaanHttp.get('/channels');
  const list = unwrap<KiaanChannel[]>(res.data);
  return Array.isArray(list) ? list : [];
}

export async function createChannel(input: { name: string; kind: 'channel' | 'direct'; memberIds?: string[] }): Promise<KiaanChannel> {
  const res = await kiaanHttp.post('/channels', input);
  return unwrap<KiaanChannel>(res.data);
}

export async function listMessages(channelId: string, after?: string): Promise<KiaanMessage[]> {
  const res = await kiaanHttp.get(`/channels/${encodeURIComponent(channelId)}/messages`, {
    params: after ? { after } : undefined,
  });
  const list = unwrap<KiaanMessage[]>(res.data);
  return Array.isArray(list) ? list : [];
}

export async function postMessage(channelId: string, body: string, files?: string[]): Promise<KiaanPosted> {
  const res = await kiaanHttp.post(`/channels/${encodeURIComponent(channelId)}/messages`, files?.length ? { body, files } : { body });
  return unwrap<KiaanPosted>(res.data);
}

export async function getChannelAgents(channelId: string): Promise<ChannelAgentState> {
  const res = await kiaanHttp.get(`/channels/${encodeURIComponent(channelId)}/agents`);
  const v = unwrap<Partial<ChannelAgentState>>(res.data) ?? {};
  return { limit: v.limit ?? 6, chain: v.chain ?? 0, writing: v.writing ?? [], agents: v.agents ?? [] };
}

export async function addChannelMember(channelId: string, memberType: 'user' | 'agent', memberId: string): Promise<KiaanChannel> {
  const res = await kiaanHttp.post(`/channels/${encodeURIComponent(channelId)}/members`, { memberType, memberId });
  return unwrap<KiaanChannel>(res.data);
}

export async function removeChannelMember(channelId: string, memberType: 'user' | 'agent', memberId: string): Promise<KiaanChannel> {
  const res = await kiaanHttp.delete(
    `/channels/${encodeURIComponent(channelId)}/members/${memberType}/${encodeURIComponent(memberId)}`,
  );
  return unwrap<KiaanChannel>(res.data);
}

export async function stopChannelAgents(channelId: string): Promise<{ stopped: number; message: KiaanMessage }> {
  const res = await kiaanHttp.post(`/channels/${encodeURIComponent(channelId)}/stop-agents`, {});
  return unwrap<{ stopped: number; message: KiaanMessage }>(res.data);
}

export async function listPeople(): Promise<KiaanPerson[]> {
  const res = await kiaanHttp.get('/people');
  const list = unwrap<KiaanPerson[]>(res.data);
  return Array.isArray(list) ? list : [];
}

// ── Report a bug ────────────────────────────────────────────────────────────

export interface BugReportInput {
  route: string;
  pageUrl: string;
  note: string;
  imageFileId?: string;
}

export async function reportBug(input: BugReportInput): Promise<{ id: string }> {
  const res = await kiaanHttp.post('/bugs', input);
  return unwrap<{ id: string }>(res.data);
}

// ── Dev mode ────────────────────────────────────────────────────────────────

export type BugStatus = 'new' | 'ai_fixing' | 'waits_ok' | 'fixed_dev' | 'closed';

export interface KiaanBug {
  id: string;
  note: string;
  route: string;
  pageUrl?: string;
  reporterName?: string;
  status: BugStatus;
  createdAt: string;
}

export interface DevWorker {
  name: string;
  role: string;
  status: 'working' | 'idle' | 'off';
  task?: string | null;
}

export interface DevWaitingItem {
  title: string;
  detail?: string;
  action: string; // "Approve" | "Send to live"
}

export interface ScreenMap {
  screen: string;
  flow: string[];
  rules: string[];
  autos: string[];
}

export interface DevOverview {
  workers: DevWorker[];
  usedPercent?: number | null;
  bugs: KiaanBug[];
  waiting: DevWaitingItem[];
  screenMap?: ScreenMap | null;
}

export interface DevTask {
  id: string;
  title: string;
  assignee?: string | null;
  status: string;
  branch?: string | null;
}

export interface KnowledgeGroup {
  key: string;
  label: string;
  count?: number;
}

export interface KnowledgePersonRule {
  name: string;
  role: string;
  extra: { group: string; allow: boolean }[];
}

export interface DevKnowledge {
  roles: string[];
  groups: KnowledgeGroup[];
  /** grid[groupKey][role] = true when that role may read the group */
  grid: Record<string, Record<string, boolean>>;
  people: KnowledgePersonRule[];
  sources?: { sorted: number; pending: number } | null;
}

export interface DevPhone {
  setup: { label: string; done: boolean }[];
  changes: { title: string; app: string; status: string }[];
  lessons: { title: string; status: string }[];
  downloads?: { mac?: string; windows?: string } | null;
}

export interface DevProduction {
  web: { up: boolean; httpStatus: number | null; checkedAt: string | null };
  appVersions: Record<string, string>;
  lastDeploy: { at: string; by?: string | null; webVersion?: string | null; backupSaved?: boolean } | null;
  waiting: { title: string; status: string }[];
  rollbackPoint: string | null;
  week?: { uptime: string; errors: string; downAlerts: string } | null;
  lane?: { branch: string | null; dev: string | null; devReady?: number } | null;
}

export type ProviderStatus = 'ok' | 'broken' | 'unset';
export type Provider = 'openrouter' | 'claude';

export interface DevSettings {
  openrouter: { status: ProviderStatus; last4: string | null };
  claude: { status: ProviderStatus; last4: string | null };
}

export interface ProviderTestResult {
  status: 'ok' | 'broken';
  last4?: string | null;
  message?: string;
}

export async function getDevOverview(): Promise<DevOverview> {
  const res = await kiaanHttp.get('/dev/overview');
  const o = unwrap<Partial<DevOverview>>(res.data) ?? {};
  return { workers: o.workers ?? [], usedPercent: o.usedPercent ?? null, bugs: o.bugs ?? [], waiting: o.waiting ?? [], screenMap: o.screenMap ?? null };
}

export async function getDevTasks(): Promise<DevTask[]> {
  const res = await kiaanHttp.get('/dev/tasks');
  const list = unwrap<DevTask[]>(res.data);
  return Array.isArray(list) ? list : [];
}

export async function getDevBugs(): Promise<KiaanBug[]> {
  const res = await kiaanHttp.get('/dev/bugs');
  const list = unwrap<KiaanBug[]>(res.data);
  return Array.isArray(list) ? list : [];
}

export async function getDevKnowledge(): Promise<DevKnowledge> {
  const res = await kiaanHttp.get('/dev/knowledge');
  const k = unwrap<Partial<DevKnowledge>>(res.data) ?? {};
  return { roles: k.roles ?? [], groups: k.groups ?? [], grid: k.grid ?? {}, people: k.people ?? [], sources: k.sources ?? null };
}

export async function getDevPhone(): Promise<DevPhone> {
  const res = await kiaanHttp.get('/dev/phone');
  const p = unwrap<Partial<DevPhone>>(res.data) ?? {};
  return { setup: p.setup ?? [], changes: p.changes ?? [], lessons: p.lessons ?? [], downloads: p.downloads ?? null };
}

export async function getDevProduction(): Promise<DevProduction> {
  const res = await kiaanHttp.get('/dev/production');
  const p = unwrap<Partial<DevProduction>>(res.data) ?? {};
  return {
    web: p.web ?? { up: false, httpStatus: null, checkedAt: null },
    appVersions: p.appVersions ?? {},
    lastDeploy: p.lastDeploy ?? null,
    waiting: p.waiting ?? [],
    rollbackPoint: p.rollbackPoint ?? null,
    week: p.week ?? null,
    lane: p.lane ?? null,
  };
}

export async function getDevSettings(): Promise<DevSettings> {
  const res = await kiaanHttp.get('/dev/settings');
  const s = unwrap<Partial<DevSettings>>(res.data) ?? {};
  return {
    openrouter: s.openrouter ?? { status: 'unset', last4: null },
    claude: s.claude ?? { status: 'unset', last4: null },
  };
}

/** Runs one test call on the server. Saves only on success. Never echoes the key. */
export async function setProviderKey(provider: Provider, key: string): Promise<ProviderTestResult> {
  const res = await kiaanHttp.post(`/dev/settings/${provider}`, { key });
  return unwrap<ProviderTestResult>(res.data);
}

// ── Knowledge upload (Task 9) ───────────────────────────────────────────────

export type RecordKind = 'STATE' | 'EVENT';

export interface SortedFile {
  name: string;
  kind: RecordKind;
}

export interface SortedGroup {
  key: string;
  label: string;
  files: SortedFile[];
  suggestedRoles: string[];
  kind: RecordKind | 'mixed';
  /** FIX 33: a group can be a branch of another group. */
  parentKey?: string | null;
}

/** FIX 33: files the sorter will never import (Aadhaar / PAN / bank statements). */
export interface RefusedFile { path: string; reason: string }

export async function sortKnowledgeFolder(files: File[]): Promise<{ groups: SortedGroup[]; refused: RefusedFile[] }> {
  const body = new FormData();
  for (const f of files) body.append('files', f, (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name);
  const res = await kiaanHttp.post('/dev/knowledge/sort', body, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  const g = unwrap<{ groups?: SortedGroup[]; refused?: RefusedFile[] }>(res.data);
  return { groups: Array.isArray(g?.groups) ? g.groups : [], refused: Array.isArray(g?.refused) ? g.refused : [] };
}

/** `rejected` (SUN-89): files the automatic knowledge check refused; they are on Knowledge → Rejected. */
export interface SaveKnowledgeResult { saved: number; unchanged?: number; rejected?: Array<{ name: string; reasons: string[]; notes: string[] }>; importId?: string | null }
export async function saveKnowledgeGroups(groups: SortedGroup[]): Promise<SaveKnowledgeResult> {
  const res = await kiaanHttp.post('/dev/knowledge/save', { groups });
  return unwrap<SaveKnowledgeResult>(res.data);
}

// ── Live work panel (SUN-55, design V1) ─────────────────────────────────────

export interface LwStep {
  text: string;
  state: 'done' | 'now' | 'todo';
}

export interface LwFinished {
  issueId: string;
  area: string;
  result: string;
  agentName: string | null;
  finishedAt: string | null;
  onDev: boolean;
  onLive: boolean;
  checked: boolean;
  /** when Claude's review said APPROVED */
  checkedAt: string | null;
  whatChanged: string[];
  howWeKnow: string[];
  leftForLater: string | null;
  route: string | null;
}

export interface LwAgent {
  id: string;
  name: string;
  initial: string;
  state: 'working' | 'free' | 'paused';
  sentence: string;
  issueId: string | null;
  startedAt: string | null;
  progress: { done: number; total: number } | null;
  steps: LwStep[];
  earlier: LwFinished[];
}

export interface LwOption {
  id: string;
  label: string;
  detail: string | null;
  recommended: boolean;
}

export interface LwNeed {
  issueId: string;
  question: string;
  detail: string | null;
  askedBy: string | null;
  askedAt: string | null;
  blocks: string;
  options: LwOption[];
}

export interface LiveWork {
  needs: LwNeed[];
  agents: LwAgent[];
  finished: LwFinished[];
  /** when the server read Kiaan Work (ISO) */
  asOf?: string;
  /** the server's first load is still running; ask again in a few seconds */
  partial?: boolean;
}

export const LIVE_WORK_TIMEOUT_MS = 20000;

export async function getLiveWork(): Promise<LiveWork> {
  // A cold load reads many task threads and can take ~8 s; the 4 s default gave up first (SUN-146).
  const res = await kiaanHttp.get('/live-work', { timeout: LIVE_WORK_TIMEOUT_MS });
  const v = unwrap<Partial<LiveWork>>(res.data) ?? {};
  return { needs: v.needs ?? [], agents: v.agents ?? [], finished: v.finished ?? [], asOf: v.asOf, partial: v.partial };
}

/** A call that ran out of time (no answer at all) is slow, not "not reachable". */
export function isSlowAnswer(err: unknown): boolean {
  const e = err as { response?: unknown; code?: string; message?: string } | null;
  if (!e || e.response) return false;
  return e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT' || /timeout/i.test(e.message ?? '');
}

/** Posts the answer as a comment on that task (server names the person). */
export async function answerNeed(issueId: string, body: { optionId?: string; text?: string }): Promise<void> {
  await kiaanHttp.post(`/live-work/needs/${encodeURIComponent(issueId)}/answer`, body);
}

export async function pauseLiveAgent(agentId: string): Promise<void> {
  await kiaanHttp.post(`/live-work/agents/${encodeURIComponent(agentId)}/pause`, {});
}

// ── Talk to an agent (SUN-57) ───────────────────────────────────────────────

export type TalkStatus = 'working' | 'ready' | 'off' | 'paused';

export interface TalkMessage {
  id: string;
  from: 'user' | 'agent';
  name: string;
  text: string;
  at: string | null;
  attachments?: ChatAttachment[];
}

export interface AgentTalk {
  agent: { id: string; name: string; status: TalkStatus };
  issue: { id: string; identifier: string | null; title: string } | null;
  messages: TalkMessage[];
  /** SUN-110: who is asking and what Kiaan · Manager answers from (SS Manager only). */
  you?: TalkYou;
  /** null for Kiaan · Manager: no credits line. */
  creditsPerMessage?: number | null;
}

export interface TalkYou { name: string; roleLabel: string; scopeText: string }

export interface TalkSent {
  issueId: string;
  identifier: string | null;
  title: string;
  created: boolean;
  woken: boolean;
  message: TalkMessage;
}

/** The thread with this agent; `issueId` keeps the poll on the task the message went to. */
export async function getAgentTalk(agentId: string, opts: { issueId?: string; after?: string } = {}): Promise<AgentTalk> {
  const res = await kiaanHttp.get(`/agents/${encodeURIComponent(agentId)}/talk`, { params: opts });
  const t = unwrap<Partial<AgentTalk>>(res.data) ?? {};
  return {
    agent: t.agent ?? { id: agentId, name: '', status: 'off' },
    issue: t.issue ?? null,
    messages: t.messages ?? [],
    you: t.you,
    creditsPerMessage: t.creditsPerMessage ?? null,
  };
}

/** Server adds it to the agent's current task (or starts one) and wakes the agent. */
export async function sendAgentTalk(agentId: string, text: string): Promise<TalkSent> {
  const res = await kiaanHttp.post(`/agents/${encodeURIComponent(agentId)}/talk`, { text });
  return unwrap<TalkSent>(res.data);
}

/** The server's own sentence (paused, too long…) when it gave one; else the usual text. */
export function kiaanServerText(err: unknown): string {
  const r = (err as { response?: { status?: number; data?: { error?: unknown } } } | null)?.response;
  if (r?.status && r.status < 500 && r.status !== 403 && typeof r.data?.error === 'string') return r.data.error;
  return kiaanErrorText(err);
}

// ── Operator (the agent with a screen) — admin only ─────────────────────────

/** Kiaan Browser (SUN-101). The route path stays /operator; only the name changed. */
export interface BrowserPerson { id: string; name: string }
export interface OperatorStatus {
  configured: boolean;
  reachable: boolean;
  busy: { kind: string; name: string } | null;
  session: { open: boolean };
  control: { heldBy: BrowserPerson | null; since?: string | null };
  login: { by: BrowserPerson | null; savedAt: string | null; sites: string[] };
  blockedHosts?: string[];
}
export interface TeachStep { n: number; text: string; dropped: boolean }
export interface TeachCurrent { recording: boolean; name: string | null; description?: string | null; steps: TeachStep[] }
export type JobState = 'draft' | 'approved' | 'changed';
export interface JobSchedule { time: string; inputs: Record<string, string>; enabled: boolean }
export interface BrowserJob {
  name: string;
  description: string | null;
  state: JobState;
  hasDraft: boolean;
  inputs: string[];
  sha256: string;
  approvedBy: string | null;
  approvedAt: string | null;
  schedule: JobSchedule | null;
  runs: { count: number; last: { runId: string; ok: boolean; at: string } | null };
}
export interface JobRunRow { runId: string; ok: boolean; at: string; trigger: 'manual' | 'schedule' }
export interface OperatorStep { n: number; name: string; ok: boolean; error?: string }
export interface OperatorRunResult { ok: boolean; skill?: string; steps?: OperatorStep[]; error?: string; startedAt?: string; finishedAt?: string }
export interface OperatorRun { runId: string; running?: boolean; result: OperatorRunResult | null; files?: { name: string; bytes: number }[] }

const OP_SLOW = { timeout: 20000 };
export const operatorFileUrl = (runId: string, file: string) => `/api/v1/kiaan/operator/runs/${encodeURIComponent(runId)}/${encodeURIComponent(file)}`;

export async function getOperatorStatus(): Promise<OperatorStatus> {
  return unwrap<OperatorStatus>((await kiaanHttp.get('/operator/status', OP_SLOW)).data);
}
/** The screen page URL for an iframe. The ticket lives 60 s and works once. */
export async function getOperatorScreenUrl(viewOnly = true): Promise<string> {
  const { ticket } = unwrap<{ ticket: string }>((await kiaanHttp.post('/operator/screen-ticket', {})).data);
  const ws = `api/v1/kiaan/operator/screen/websockify?ticket=${ticket}`;
  return `/api/v1/kiaan/operator/screen/vnc.html?autoconnect=1&resize=scale&reconnect=0&view_only=${viewOnly ? 1 : 0}&path=${encodeURIComponent(ws)}`;
}
const jobUrl = (name: string, tail: string) => `/operator/jobs/${encodeURIComponent(name)}${tail}`;
export async function takeBrowserControl(): Promise<{ heldBy: BrowserPerson }> {
  return unwrap((await kiaanHttp.post('/operator/control/take', {}, OP_SLOW)).data);
}
export async function releaseBrowserControl(): Promise<{ login: OperatorStatus['login'] }> {
  return unwrap((await kiaanHttp.post('/operator/control/release', {}, OP_SLOW)).data);
}
export async function startTeach(name: string, description: string, url: string): Promise<void> {
  await kiaanHttp.post('/operator/teach/start', { name, description, url }, OP_SLOW);
}
export async function getTeachCurrent(): Promise<TeachCurrent> {
  return unwrap<TeachCurrent>((await kiaanHttp.get('/operator/teach/current', OP_SLOW)).data);
}
export async function undoTeachStep(): Promise<void> {
  await kiaanHttp.post('/operator/teach/undo', {}, OP_SLOW);
}
export async function stopTeach(): Promise<{ taskId: string; identifier: string | null }> {
  return unwrap((await kiaanHttp.post('/operator/teach/stop', {}, { timeout: 30000 })).data);
}
export async function listJobs(): Promise<BrowserJob[]> {
  return unwrap<BrowserJob[]>((await kiaanHttp.get('/operator/jobs', OP_SLOW)).data) ?? [];
}
export async function getJobSource(name: string, from: 'draft' | 'approved'): Promise<string> {
  return unwrap<{ source: string }>((await kiaanHttp.get(jobUrl(name, '/source'), { ...OP_SLOW, params: { from } })).data).source;
}
export async function saveJobDraft(name: string, source: string): Promise<void> {
  await kiaanHttp.put(jobUrl(name, '/draft'), { source }, OP_SLOW);
}
export async function approveJob(name: string): Promise<void> {
  await kiaanHttp.post(jobUrl(name, '/approve'), {}, OP_SLOW);
}
export async function runJob(name: string, inputs: Record<string, string>): Promise<{ runId: string }> {
  return unwrap((await kiaanHttp.post(jobUrl(name, '/run'), { inputs }, OP_SLOW)).data);
}
export async function setJobSchedule(name: string, time: string | null, inputs: Record<string, string>): Promise<void> {
  await kiaanHttp.put(jobUrl(name, '/schedule'), { time, inputs }, OP_SLOW);
}
export async function listJobRuns(name: string): Promise<JobRunRow[]> {
  return unwrap<JobRunRow[]>((await kiaanHttp.get(jobUrl(name, '/runs'), OP_SLOW)).data) ?? [];
}
export async function getRun(runId: string): Promise<OperatorRun> {
  return unwrap<OperatorRun>((await kiaanHttp.get(`/operator/runs/${encodeURIComponent(runId)}`, OP_SLOW)).data);
}
/** Plain words for the server's answer (409 busy / not approved, 400 etc.). */
export function operatorErrorText(err: unknown): string {
  const r = (err as { response?: { status?: number; data?: { error?: string } } } | null)?.response;
  if (r?.data?.error) return r.data.error;
  return kiaanErrorText(err);
}

// ── System monitor (SUN-69) — admin only, the box-monitor numbers through AURA ─────────────────────────────────

export type SysBox = 'dev' | 'live';
export type SysTag = 'never' | 'pause_first' | 'pause_second' | 'ask_first' | 'live_watch_only';
export interface SysTarget {
  id: string;
  label: string;
  tag: SysTag | string | null;
  kind?: string | null;
  mem: number | null;
  cpu: number | null;
  state: string | null;
  runs?: number | null;
  maxRuns?: number | null;
  parts?: number | null;
  jobs?: number | null;
  pausedBy?: string | null;
  pausedAt?: string | null;
}
export interface SysState {
  cores: number;
  cpu: number | null;
  load: number[] | null;
  mem: { total: number; available: number } | null;
  swap: { total: number; free: number } | null;
  disk: { total: number; free: number } | null;
  site: { status: number | null; seconds: number | null } | null;
  automation: boolean;
  cpuHold?: boolean;
  targets: SysTarget[];
}
export interface SysStateReply { state: SysState | null; liveLastSeen: string | null }
export interface SysPoint { at: string; cpu: number | null; load: number | null; memAvail: number | null; swapFree: number | null }
export interface SysEvent { at: string; box: SysBox; level: 'action' | 'alert' | 'info' | 'error'; text: string; by?: string | null }
export interface SysRule { id: string; text: string; on: boolean }
export interface SysRules { automation: boolean; rules: SysRule[] }
export type SysTaskTag = 'priority' | 'can-wait' | 'none';
export interface SysTask { id: string; identifier: string | null; title: string; status: string | null; agent: string | null; tag: SysTaskTag }

const SYS = { timeout: 8000 };
export async function getSysState(box: SysBox): Promise<SysStateReply> {
  return unwrap<SysStateReply>((await kiaanHttp.get('/system/state', { ...SYS, params: { box } })).data);
}
export async function getSysHistory(box: SysBox): Promise<SysPoint[]> {
  return unwrap<{ points?: SysPoint[] }>((await kiaanHttp.get('/system/history', { ...SYS, params: { box } })).data)?.points ?? [];
}
export async function getSysEvents(box: SysBox): Promise<SysEvent[]> {
  const v = unwrap<{ events?: SysEvent[] } | SysEvent[]>((await kiaanHttp.get('/system/events', { ...SYS, params: { box, limit: 20 } })).data);
  return Array.isArray(v) ? v : v?.events ?? [];
}
export async function getSysRules(): Promise<SysRules> {
  return unwrap<SysRules>((await kiaanHttp.get('/system/rules', SYS)).data);
}
export async function setSysRules(patch: { automation?: boolean; rules?: Record<string, boolean> }): Promise<void> {
  await kiaanHttp.post('/system/rules', { ...patch, box: 'dev' }, SYS);
}
export async function sysTargetAction(id: string, action: 'pause' | 'resume'): Promise<void> {
  await kiaanHttp.post(`/system/targets/${encodeURIComponent(id)}/${action}`, { box: 'dev' }, SYS);
}
export async function setSysTargetTag(id: string, tag: Exclude<SysTag, 'live_watch_only'>): Promise<void> {
  await kiaanHttp.post(`/system/targets/${encodeURIComponent(id)}/tag`, { tag, box: 'dev' }, SYS);
}
export async function getSysTasks(): Promise<SysTask[]> {
  return unwrap<SysTask[]>((await kiaanHttp.get('/system/tasks', SYS)).data) ?? [];
}
export async function setSysTaskTag(issueId: string, tag: SysTaskTag): Promise<void> {
  await kiaanHttp.post(`/system/tasks/${encodeURIComponent(issueId)}/tag`, { tag }, SYS);
}
export async function requestLiveRestart(): Promise<{ identifier: string | null; existing: boolean }> {
  return unwrap((await kiaanHttp.post('/system/live/restart-request', {}, SYS)).data);
}
/** The server's own sentence for a refused action (locked row, live read-only), else the usual text. */
export function sysErrorText(err: unknown): string {
  const r = (err as { response?: { status?: number; data?: { error?: string } } } | null)?.response;
  if (r?.data?.error && r.status && r.status !== 401) return r.data.error;
  return kiaanErrorText(err);
}

// ── Chat threads with an agent (SUN-61 backend, SUN-70 widget) — admin only ────────────────────────────

export interface AgentThread {
  id: string;
  identifier: string | null;
  name: string;
  archived: boolean;
  isDefault?: boolean;
  mine?: boolean;
  lastMessageAt: string | null;
  unread: number;
  agent?: { id: string; name: string; status: TalkStatus };
}

export interface AgentThreadList {
  agent: { id: string; name: string; status: TalkStatus } | null;
  threads: AgentThread[];
}

export interface ThreadMessages {
  thread: { id: string; identifier: string | null; name: string; archived: boolean } | null;
  agent: { id: string; name: string; status: TalkStatus } | null;
  messages: TalkMessage[];
}

export async function listAgentThreads(agentId: string, archived = false): Promise<AgentThreadList> {
  const res = await kiaanHttp.get(`/agents/${encodeURIComponent(agentId)}/threads`, { params: archived ? { archived: 1 } : undefined });
  const v = unwrap<Partial<AgentThreadList>>(res.data) ?? {};
  return { agent: v.agent ?? null, threads: Array.isArray(v.threads) ? v.threads : [] };
}

/** A new thread starts with its first message; the server wakes the agent on it. */
export async function createAgentThread(agentId: string, text: string, name?: string): Promise<{ thread: AgentThread; message: TalkMessage }> {
  const res = await kiaanHttp.post(`/agents/${encodeURIComponent(agentId)}/threads`, name ? { name, text } : { text }, { timeout: 15000 });
  const v = unwrap<{ thread: Partial<AgentThread> & { id: string; name: string }; message: TalkMessage }>(res.data);
  return {
    thread: { identifier: null, archived: false, unread: 0, lastMessageAt: v.message?.at ?? null, ...v.thread },
    message: v.message,
  };
}

export async function updateAgentThread(threadId: string, patch: { name?: string; archived?: boolean }): Promise<AgentThread> {
  const res = await kiaanHttp.patch(`/threads/${encodeURIComponent(threadId)}`, patch);
  return unwrap<AgentThread>(res.data);
}

export async function getThreadMessages(threadId: string): Promise<ThreadMessages> {
  const res = await kiaanHttp.get(`/threads/${encodeURIComponent(threadId)}/messages`);
  const v = unwrap<Partial<ThreadMessages>>(res.data) ?? {};
  return { thread: v.thread ?? null, agent: v.agent ?? null, messages: Array.isArray(v.messages) ? v.messages : [] };
}

export async function sendThreadMessage(threadId: string, text: string, files?: string[]): Promise<TalkMessage> {
  const res = await kiaanHttp.post(`/threads/${encodeURIComponent(threadId)}/messages`, files?.length ? { text, files } : { text }, { timeout: 15000 });
  return unwrap<{ message: TalkMessage }>(res.data).message;
}

// ── Files and pictures in chats (SUN-82) ────────────────────────────────────

async function uploadFiles(path: string, files: File[]): Promise<ChatAttachment[]> {
  const form = new FormData();
  for (const f of files) form.append('files', f, f.name || 'pasted-image.png');
  // No JSON header: the browser must set the multipart boundary itself.
  const res = await kiaanHttp.post(path, form, { timeout: 120000, headers: { 'Content-Type': undefined } as never });
  return unwrap<{ files: ChatAttachment[] }>(res.data).files ?? [];
}

export const uploadThreadFiles = (threadId: string, files: File[]) => uploadFiles(`/threads/${encodeURIComponent(threadId)}/files`, files);
export const uploadChannelFiles = (channelId: string, files: File[]) => uploadFiles(`/channels/${encodeURIComponent(channelId)}/files`, files);

// ── Lesson cards and requests (SUN-110c; contract in SUN-110) ───────────────

/** `GET /kiaan/lessons/<id>` — only a lesson this reader may see. 403/404 → the card is not shown. */
export interface KiaanLesson {
  id: string;
  title: string;
  moduleId?: string;
  minutes: number;
  platform: string;
  showMe: { allowed: boolean };
  videoUrl: string | null;
  lessonUrl: string;
}

export type LessonLookup =
  | { state: 'ok'; lesson: KiaanLesson }
  | { state: 'forbidden' }
  | { state: 'missing' };

/**
 * Never throws. The server answers 404 for "not allowed" and "unknown" alike (on purpose), and may say 403:
 * both → 'forbidden' (no card; "Send to admin" may be offered, the server refuses unknown ids itself).
 * Any other failure → 'missing' so no card shows and no button is offered.
 */
export async function getKiaanLesson(id: string): Promise<LessonLookup> {
  try {
    const res = await kiaanHttp.get(`/lessons/${encodeURIComponent(id)}`);
    const lesson = unwrap<Partial<KiaanLesson> | null>(res.data);
    if (!lesson || typeof lesson.id !== 'string' || typeof lesson.title !== 'string') return { state: 'missing' };
    return {
      state: 'ok',
      lesson: {
        id: lesson.id,
        title: lesson.title,
        moduleId: lesson.moduleId,
        minutes: typeof lesson.minutes === 'number' ? lesson.minutes : 0,
        platform: lesson.platform ?? '',
        showMe: { allowed: lesson.showMe?.allowed === true },
        videoUrl: lesson.videoUrl ?? null,
        lessonUrl: lesson.lessonUrl ?? `/tour/lesson/${lesson.id}`,
      },
    };
  } catch (err) {
    const status = (err as { response?: { status?: number } } | null)?.response?.status;
    return status === 403 || status === 404 ? { state: 'forbidden' } : { state: 'missing' };
  }
}

/** One row of GET /kiaan/requests: pending AND decided ones (admin/owner only). `by` is a user id. */
export interface KiaanRequest {
  id: string;
  identifier: string | null;
  kind: string;
  title: string;
  detail: string;
  by: string;
  lessonId: string | null;
  state: 'pending' | 'approved' | 'rejected';
  createdAt: string | null;
}

/** "Send to admin" on a lesson this person may not see. */
export async function createLessonAccessRequest(lessonId: string): Promise<void> {
  await kiaanHttp.post('/requests', { kind: 'lesson-access', lessonId });
}

/** Admin/owner only (the server refuses anyone else). Only the requests still waiting for a decision. */
export async function listRequestsWaiting(): Promise<KiaanRequest[]> {
  const res = await kiaanHttp.get('/requests');
  const v = unwrap<{ requests?: KiaanRequest[] } | KiaanRequest[] | null>(res.data);
  const list = Array.isArray(v) ? v : v?.requests ?? [];
  return list.filter((r) => r.state === 'pending');
}

export async function decideRequest(id: string, decision: 'approve' | 'reject'): Promise<void> {
  await kiaanHttp.post(`/requests/${encodeURIComponent(id)}/${decision}`, {});
}
